package kpt.core.database.currency.dao

import androidx.room3.EntityDeleteOrUpdateAdapter
import androidx.room3.EntityInsertAdapter
import androidx.room3.EntityUpsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.coroutines.createFlow
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.reflect.KClass
import kotlinx.coroutines.flow.Flow
import kpt.core.database.currency.entity.RateHistoryEntity

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "OPT_IN_USAGE_ERROR", "OPT_IN_USAGE", "MemberExtensionConflict", "CAN_BE_VAL"])
internal class RateHistoryDao_Impl(
  __db: RoomDatabase,
) : RateHistoryDao {
  private val __db: RoomDatabase

  private val __upsertAdapterOfRateHistoryEntity: EntityUpsertAdapter<RateHistoryEntity>
  init {
    this.__db = __db
    this.__upsertAdapterOfRateHistoryEntity = EntityUpsertAdapter<RateHistoryEntity>(object : EntityInsertAdapter<RateHistoryEntity>() {
      protected override fun createQuery(): String = "INSERT INTO `rate_history` (`fromCurrency`,`toCurrency`,`startDate`,`endDate`,`ratesJson`,`fetchedAt`) VALUES (?,?,?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: RateHistoryEntity) {
        statement.bindText(1, entity.fromCurrency)
        statement.bindText(2, entity.toCurrency)
        statement.bindText(3, entity.startDate)
        statement.bindText(4, entity.endDate)
        statement.bindText(5, entity.ratesJson)
        statement.bindLong(6, entity.fetchedAt)
      }
    }, object : EntityDeleteOrUpdateAdapter<RateHistoryEntity>() {
      protected override fun createQuery(): String = "UPDATE `rate_history` SET `fromCurrency` = ?,`toCurrency` = ?,`startDate` = ?,`endDate` = ?,`ratesJson` = ?,`fetchedAt` = ? WHERE `fromCurrency` = ? AND `toCurrency` = ? AND `startDate` = ? AND `endDate` = ?"

      protected override fun bind(statement: SQLiteStatement, entity: RateHistoryEntity) {
        statement.bindText(1, entity.fromCurrency)
        statement.bindText(2, entity.toCurrency)
        statement.bindText(3, entity.startDate)
        statement.bindText(4, entity.endDate)
        statement.bindText(5, entity.ratesJson)
        statement.bindLong(6, entity.fetchedAt)
        statement.bindText(7, entity.fromCurrency)
        statement.bindText(8, entity.toCurrency)
        statement.bindText(9, entity.startDate)
        statement.bindText(10, entity.endDate)
      }
    })
  }

  public override suspend fun upsert(entity: RateHistoryEntity): Unit = performSuspending(__db, false, true) { _connection ->
    __upsertAdapterOfRateHistoryEntity.upsert(_connection, entity)
  }

  public override fun `get`(
    from: String,
    to: String,
    startDate: String,
    endDate: String,
  ): Flow<RateHistoryEntity?> {
    val _sql: String = """
        |
        |        SELECT * FROM rate_history
        |        WHERE fromCurrency = ? AND toCurrency = ?
        |        AND startDate = ? AND endDate = ?
        |        LIMIT 1
        |        
        """.trimMargin()
    return createFlow(__db, false, arrayOf("rate_history")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, from)
        _argIndex = 2
        _stmt.bindText(_argIndex, to)
        _argIndex = 3
        _stmt.bindText(_argIndex, startDate)
        _argIndex = 4
        _stmt.bindText(_argIndex, endDate)
        val _columnIndexOfFromCurrency: Int = getColumnIndexOrThrow(_stmt, "fromCurrency")
        val _columnIndexOfToCurrency: Int = getColumnIndexOrThrow(_stmt, "toCurrency")
        val _columnIndexOfStartDate: Int = getColumnIndexOrThrow(_stmt, "startDate")
        val _columnIndexOfEndDate: Int = getColumnIndexOrThrow(_stmt, "endDate")
        val _columnIndexOfRatesJson: Int = getColumnIndexOrThrow(_stmt, "ratesJson")
        val _columnIndexOfFetchedAt: Int = getColumnIndexOrThrow(_stmt, "fetchedAt")
        val _result: RateHistoryEntity?
        if (_stmt.step()) {
          val _tmpFromCurrency: String
          _tmpFromCurrency = _stmt.getText(_columnIndexOfFromCurrency)
          val _tmpToCurrency: String
          _tmpToCurrency = _stmt.getText(_columnIndexOfToCurrency)
          val _tmpStartDate: String
          _tmpStartDate = _stmt.getText(_columnIndexOfStartDate)
          val _tmpEndDate: String
          _tmpEndDate = _stmt.getText(_columnIndexOfEndDate)
          val _tmpRatesJson: String
          _tmpRatesJson = _stmt.getText(_columnIndexOfRatesJson)
          val _tmpFetchedAt: Long
          _tmpFetchedAt = _stmt.getLong(_columnIndexOfFetchedAt)
          _result = RateHistoryEntity(_tmpFromCurrency,_tmpToCurrency,_tmpStartDate,_tmpEndDate,_tmpRatesJson,_tmpFetchedAt)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun delete(from: String, to: String) {
    val _sql: String = "DELETE FROM rate_history WHERE fromCurrency = ? AND toCurrency = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, from)
        _argIndex = 2
        _stmt.bindText(_argIndex, to)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteAll() {
    val _sql: String = "DELETE FROM rate_history"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
